import { get } from 'lodash';
import getMetricsData from '@api/metricContainer';
import instance from '@api/axiosInstance';
import { getInvertedPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import { formatTimeseriesData } from '@src/components/metrics-panel-hz/utils/format';
import useFlowQuery, { createQuery } from '../../hooks/useFlowQuery';
import useQueryParams from '../../hooks/useQueryParams';
import { appendFlowIdentifier } from '@utils/filter';
import { useMemo } from 'react';
import { panelsAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { calcDodInterval } from '../../utils';

const getFlowMetrics = (flowMeta) => {
  if (!flowMeta || !flowMeta.metricsMeta) return [];
  const { metricsMeta } = flowMeta;

  try {
    return [
      ...new Set(
        Object.keys(metricsMeta).reduce((acc, key) => {
          if (metricsMeta[key] && metricsMeta[key].id) {
            acc.push(metricsMeta[key].id);
          }
          return acc;
        }, [] as string[]),
      ),
    ];
  } catch (error) {
    console.error('Error getting flow metrics:', error);
    return [];
  }
};

function useFetchDodFlowData({
  flowId,
  flowMeta,
  enabled = true,
}: {
  flowId: string;
  flowMeta?: any;
  enabled?: boolean;
}) {
  const { identifier } = flowMeta || {};
  const panels = useAtomValue(panelsAtom);

  const metrics = getFlowMetrics(flowMeta);
  const { interval, filter, _rKey, allowedMetrics } = useQueryParams();

  const isDeleted = flowMeta?.isDeleted;

  const filteredMetrics = metrics.filter(
    (metric) =>
      (metric && allowedMetrics && metric in allowedMetrics) ||
      (metric &&
        allowedMetrics &&
        getInvertedPercentageMetricByKey({ metricKey: metric }) in allowedMetrics),
  );

  const allowedMetricList = [...new Set([...filteredMetrics])];

  // Calculate comparison interval (previous period)
  const comparisonInterval = useMemo(() => calcDodInterval(interval), [interval]);

  const options = {};

  const payload = comparisonInterval
    ? createQuery(
        allowedMetricList,
        `${comparisonInterval.from}/${comparisonInterval.to}`,
        comparisonInterval.granularity,
        appendFlowIdentifier(filter, identifier) as any,
        options,
      )
    : null;

  const result = useFlowQuery({
    flowId: `${flowId}-dod`,
    queryKey: ['overview', 'flow-dod', payload?.queries, _rKey],
    queryFn: () =>
      payload
        ? getMetricsData(payload, {}, instance).then((response) => {
            const { data, totals, annotations } = get(response, ['data', '0'], {
              totals: {},
              data: [],
              annotations: [],
            });
            const formattedData = formatTimeseriesData({
              metrics: allowedMetricList,
              data,
              interval: comparisonInterval,
              annotations,
            });
            return { data: formattedData, totals };
          })
        : Promise.resolve({ data: {}, totals: {} }),
    options: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false, // Prevent refetch when panel is shown
      enabled: enabled && !!metrics.length && !isDeleted && !!comparisonInterval && panels.flows,
      keepPreviousData: true, // Preserve data when panel is hidden/shown
      staleTime: 5 * 60 * 1000, // 5m
    },
  });

  if (isDeleted || !enabled || !comparisonInterval) {
    return {
      data: null,
      isLoading: false,
      isFetching: false,
      isError: false,
      error: null,
      refetch: () => Promise.resolve(),
      queued: false,
      comparisonInterval,
    };
  }

  return {
    ...result,
    queued: result.queued || false,
    comparisonInterval,
  };
}

export default useFetchDodFlowData;
